import React from "react";

/**
 * Route configuration interface.
 *
 * `imports` must be a FACTORY FUNCTION (not an evaluated import()) so that
 * webpack only fetches the chunk when React actually renders the route —
 * achieving true lazy loading. Using `() => import(...)` instead of
 * `import(...)` keeps all feature modules out of the main bundle.
 */
export interface IRoute {
  auth?: boolean;
  exact?: boolean;
  path: string;
  noHeader?: boolean;
  noFooter?: boolean;
  /** Zero-argument factory returning a dynamic import. Must use static paths. */
  imports: () => Promise<{ default: React.ComponentType<any> }>;
  noPopup?: boolean;
}

const ROUTES: IRoute[] = [
  // ─── Home (EAGERLY loaded in Router.tsx — the imports factory below is a ───
  // ─── no-op for Router but kept for interface consistency)                 ───
  {
    auth: false,
    exact: true,
    path: "/home",
    imports: () => import("features/home/Home"),
    noHeader: false,
    noFooter: false,
  },
  {
    auth: false,
    exact: true,
    path: "/login",
    imports: () => import("features/login/Login"),
    noHeader: true,
    noFooter: true,
  },
  {
    auth: false,
    exact: false,
    path: "/reset-password",
    imports: () => import("features/reset-password/ResetPassword"),
    noHeader: true,
    noFooter: true,
    noPopup: true,
  },
  {
    auth: false,
    exact: false,
    path: "/verify-email",
    imports: () => import("features/verify-email/VerifyEmail"),
    noPopup: true,
  },
  {
    auth: false,
    exact: false,
    path: "/register",
    imports: () => import("features/register/Register"),
    noHeader: true,
    noFooter: true,
  },
  {
    auth: false,
    exact: true,
    path: "/forgot-password",
    imports: () => import("features/forgot-password/ForgotPassword"),
    noHeader: true,
    noFooter: true,
  },
  {
    auth: false,
    path: "/not-found",
    imports: () => import("components/NotFound/NotFound"),
    noHeader: false,
    noFooter: false,
    noPopup: true,
  },
  // ─── Root "/" (EAGERLY loaded in Router.tsx) ─────────────────────────────
  {
    auth: false,
    path: "/",
    imports: () => import("features/home/Home"),
    noHeader: false,
    noFooter: false,
  },
  {
    auth: true,
    exact: true,
    path: "/profile",
    imports: () => import("features/profile/Profile"),
  },
  {
    auth: false,
    path: "/contact-us",
    imports: () => import("features/contact-us/Contact"),
    noHeader: false,
    noFooter: false,
  },
  {
    auth: false,
    path: "/about-us",
    imports: () => import("features/about-us/AboutUs"),
    noHeader: false,
    noFooter: false,
  },
  {
    auth: false,
    path: "/our-services",
    imports: () => import("features/our-services/OurServices"),
    noFooter: false,
  },
  {
    auth: false,
    path: "/book-a-stay",
    imports: () => import("features/search-property/SearchProperty"),
    noFooter: false,
  },
  {
    auth: false,
    path: "/locations",
    imports: () => import("features/locations/Locations"),
    noHeader: false,
    noFooter: false,
  },
  {
    auth: false,
    path: "/search-results",
    imports: () => import("features/search-result/SearchResult"),
    noHeader: false,
    noFooter: false,
  },
  {
    auth: false,
    path: "/offers",
    exact: true,
    imports: () => import("features/offers/Offers"),
    noHeader: false,
    noFooter: false,
  },
  {
    auth: false,
    path: "/offers/:slug",
    exact: true,
    imports: () => import("features/offer-listing/OfferListing"),
    noHeader: false,
    noFooter: false,
  },
  {
    auth: false,
    path: "/stay-details",
    exact: false,
    imports: () => import("features/stay-details/StayDetails"),
    noHeader: false,
    noFooter: false,
  },
  {
    auth: false,
    path: "/guest-checkout",
    exact: true,
    imports: () => import("features/guest-checkout/GuestCheckout"),
    noHeader: true,
    noFooter: true,
    noPopup: true,
  },
  {
    path: "/book-room",
    exact: true,
    imports: () => import("features/payment/BookRoom"),
    noHeader: false,
    noFooter: false,
    noPopup: true,
  },
  {
    path: "/payment-success",
    exact: true,
    imports: () => import("features/payment-success/PaymentSuccess"),
    noHeader: false,
    noFooter: false,
    noPopup: true,
  },
  {
    path: "/list-property",
    exact: true,
    imports: () => import("features/list-property/ListProperty"),
    noHeader: false,
    noFooter: false,
  },
  {
    path: "/partner-with-us",
    exact: true,
    imports: () => import("features/be-our-partner/ListProperty"),
    noHeader: false,
    noFooter: false,
  },
  // ─── Owner chunk — prefetched for owner-role users (see Router.tsx) ──────
  {
    path: "/owner",
    exact: false,
    imports: () => import("features/owner/Owner"),
    noHeader: false,
    noFooter: true,
    noPopup: true,
  },
  {
    path: "/cancellation",
    exact: true,
    imports: () => import("features/cancellation/Cancellation"),
    noHeader: false,
    noFooter: false,
  },
  {
    path: "/payment-and-refund",
    exact: true,
    imports: () => import("features/payment-refund/PaymentRefund"),
    noHeader: false,
    noFooter: false,
  },
  {
    path: "/check-in",
    exact: false,
    imports: () => import("features/check-in/CheckInComponent"),
    noHeader: false,
    noFooter: false,
  },
  {
    path: "/privacy",
    exact: true,
    imports: () => import("features/privacy/Privacy"),
    noHeader: false,
    noFooter: false,
  },
];

export default ROUTES;
